import { BarItem, BarLabelContext } from '@mui/x-charts';
import {
  getDisplayValueForMaybeUndefinedValue,
  percentage,
  sum,
} from '../../../../../../utils';
import { formatPercentageNumber } from '../../../../../../utils-display';
import { SortTypes } from '../../../../../types';
import { generateUniqueTicksFromMinMax } from '../../../../../utils';
import {
  AxisConfigType,
  BarChartLayouts,
  ChartData,
  SingleAxisConfig,
  XAxisConfig,
  YAxisConfig,
} from '../common/common-chart-types';
import { getSortedLabels } from '../common/common-chart-utils/common-chart-utils';
import {
  MuiBarChartDataConfig,
  ProcessChartDataOptions,
} from './bar-chart-types';

export const getMuiBarChartDataConfigFromChartData = (
  chartData: ChartData,
  options: ProcessChartDataOptions
): MuiBarChartDataConfig => {
  // if empty data, return empty config
  if (!chartData.labels.length) {
    return {
      dataset: [],
      series: [],
      xAxisConfigs: {
        type: AxisConfigType.SINGLE,
        firstAxisConfig: {
          dataKey: 'axisId',
          id: 'xAxis',
          scaleType: 'band',
        },
      },
    };
  }
  const { layout, selectedSortOrder = SortTypes.ASC } = options;
  const { dataMap, labels } = chartData;
  const sortedLabels = selectedSortOrder
    ? getSortedLabels(chartData, selectedSortOrder)
    : labels;
  const xAxisConfigs: SingleAxisConfig<XAxisConfig<string>> = {
    type: AxisConfigType.SINGLE,
    firstAxisConfig: {
      dataKey: 'axisId',
      id: 'xAxis',
      scaleType: 'band',
      // TODO Shaq: Not sure if I want this here, but maybe I do. If I don't want this here,
      // Will have to change the dataMap to support undefined values.
      valueFormatter: (value) => getDisplayValueForMaybeUndefinedValue(value),
      tickLabelInterval: () => true,
    },
  };
  let dataset: MuiBarChartDataConfig['dataset'];
  let series: MuiBarChartDataConfig['series'];
  const stack =
    layout === BarChartLayouts.STACKED || layout === BarChartLayouts.PERCENTAGE
      ? 'total'
      : undefined;
  const { datasets } = chartData;
  dataset = sortedLabels.map((label) => {
    const dataMapValues = dataMap[label];
    let datasetValues: Record<string, number>;
    if (layout === BarChartLayouts.PERCENTAGE) {
      const total = sum(dataMapValues.map((data) => data.value));
      datasetValues = Object.fromEntries(
        dataMapValues.map((data) => [
          data.datasetKey,
          percentage(data.value, total),
        ])
      );
    } else {
      datasetValues = Object.fromEntries(
        dataMapValues.map((data) => [data.datasetKey, data.value])
      );
    }
    return {
      axisId: label,
      ...datasetValues,
    };
  });
  series = datasets.map((dataset) => ({
    id: dataset.value,
    dataKey: dataset.value,
    label:
      dataset.label ?? getDisplayValueForMaybeUndefinedValue(dataset.value),
    stack,
    stackOffset: 'none',
    stackOrder: 'reverse',
    valueFormatter: (val: number | null) => {
      const value = val ?? 0;
      if (layout === BarChartLayouts.PERCENTAGE) {
        return formatPercentageNumber(value);
      } else {
        return dataset.valueFormatter?.(value) ?? String(value);
      }
    },
  }));
  let seriesValues: number[] = [];
  if (layout === BarChartLayouts.STACKED) {
    seriesValues = dataset.map((d) => {
      return sum(series.map((s) => (s.dataKey ? (d[s.dataKey] as number) : 0)));
    });
  } else {
    seriesValues = series.flatMap((s) =>
      (dataset ?? []).map((d) => {
        return s.dataKey ? (d[s.dataKey] as number) : 0;
      })
    );
  }

  const validSeriesValues = seriesValues.filter(
    (v): v is number => v !== null && v !== undefined
  );
  const hasNegativeValues = validSeriesValues.some((d) => d < 0);
  const min = hasNegativeValues ? Math.min(...validSeriesValues) : 0;
  const max =
    validSeriesValues.length > 0 ? Math.max(...validSeriesValues) : 100;

  const yAxisValueFormatter =
    layout === BarChartLayouts.PERCENTAGE
      ? (value: number) => formatPercentageNumber(value)
      : (value: number) => {
          // using first dataset to determine y axis value configs
          // TODO: Improve the whole structure to support secondary axis
          const dataset = datasets[0];
          return dataset.valueFormatter?.(value) ?? String(value);
        };

  const yAxisConfigs: SingleAxisConfig<YAxisConfig<number>> | undefined =
    layout === BarChartLayouts.PERCENTAGE
      ? {
          type: AxisConfigType.SINGLE,
          firstAxisConfig: {
            id: 'yAxis',
            position: 'left',
            min: 0,
            max: 100,
            valueFormatter: yAxisValueFormatter,
            tickInterval: generateUniqueTicksFromMinMax(
              0,
              100,
              yAxisValueFormatter
            ),
          },
        }
      : {
          type: AxisConfigType.SINGLE,
          firstAxisConfig: {
            id: 'yAxis',
            position: 'left',
            min: hasNegativeValues ? undefined : 0, // If there is any negative number in the series, use the default min based on the data. Otherwise, use zero.
            valueFormatter: yAxisValueFormatter,
            tickInterval: generateUniqueTicksFromMinMax(
              min,
              max,
              yAxisValueFormatter
            ),
          },
        };

  return {
    dataset,
    xAxisConfigs,
    yAxisConfigs,
    series,
  };
};

const valueFormatter = (
  value: number | null,
  showPercentage: boolean,
  valFormatter?: (value: number | null) => string
) => {
  if (value === null) {
    return '';
  }
  return showPercentage
    ? formatPercentageNumber(value)
    : (valFormatter?.(value) ?? String(value));
};

// Bars shorter than this can't fit a label inside, so the label is drawn above the bar instead
export const BAR_LABEL_MIN_INSIDE_HEIGHT = 16;
// Distance from the top of the bar to the label's vertical center
const BAR_LABEL_ABOVE_OFFSET = 8;

// Same rounding as MUI's BarPlot, so this matches the rendered bar height once animation settles
export const getFinalBarHeight = (
  stackedValues: [number, number] | undefined,
  yScale: (value: number) => number | undefined
) => {
  if (!stackedValues) {
    return null;
  }
  const coordinates = stackedValues.map((v) => yScale(v));
  if (coordinates.some((c) => c === undefined || Number.isNaN(c))) {
    return null;
  }
  const valueCoordinates = coordinates as number[];
  return (
    Math.round(Math.max(...valueCoordinates)) -
    Math.round(Math.min(...valueCoordinates))
  );
};

// Only moves labels of bars whose final height is too short to hold them; every
// other label keeps MUI's centered position, including while bars animate in.
// y and animatedBarHeight are the animated label center and bar height.
export const getBarLabelY = (
  y: number,
  animatedBarHeight: number,
  finalBarHeight: number | null,
  value: number | null
) => {
  if (
    value !== null &&
    value >= 0 &&
    finalBarHeight !== null &&
    finalBarHeight < BAR_LABEL_MIN_INSIDE_HEIGHT
  ) {
    return y - animatedBarHeight / 2 - BAR_LABEL_ABOVE_OFFSET;
  }
  return y;
};

export const getBarLabelConfig = ({
  series,
  showPercentage,
  placeSmallLabelsAbove = false,
}: {
  series: MuiBarChartDataConfig['series'];
  showPercentage: boolean;
  // Only for vertical, non-stacked bars rendered with BarLabelWithSmallBarPlacement
  placeSmallLabelsAbove?: boolean;
}) => {
  return (item: BarItem, context: BarLabelContext) => {
    const muiFormatter = series.find(
      (s) => s.id === item.seriesId
    )?.valueFormatter;
    const canPlaceAbove =
      placeSmallLabelsAbove && item.value !== null && item.value >= 0;
    if (context.bar.height < 10 && !canPlaceAbove) {
      return null;
    }
    if (
      item.value &&
      ((context.bar.width < 35 && item.value > 1000) ||
        (context.bar.width < 25 && item.value > 100))
    ) {
      return null;
    }
    const valFormatter = muiFormatter
      ? (value: number | null) =>
          muiFormatter(value, { dataIndex: item.dataIndex ?? 0 }) ?? ''
      : undefined;
    const formattedValue = valueFormatter(
      item.value ?? null,
      showPercentage,
      valFormatter
    );
    return formattedValue;
  };
};
